/**
 * The channel registry — the single declarative description of every telemetry
 * channel RIVENA knows how to talk about.
 *
 * The same table is read by the API (validation, aggregation, report headers) and by
 * the web app (labels, units, decimals). Two tables would drift, and the drift would
 * be silent: a report headed "L/100km" summing a channel the API aggregates as a
 * running total is wrong in a way no test catches unless one table owns both facts.
 *
 * `availability` is the field the UI needs most. Without it a vehicle whose adapter
 * cannot produce engine hours renders a blank cell that is indistinguishable from a
 * broken query, so the fleet manager opens a support ticket. With it the UI says
 * "Données CAN non disponibles pour ce véhicule — vérifiez le numéro de programme".
 *
 * Channel keys are plain strings, not a literal union, because unmapped AVL ids
 * legitimately produce keys that no table can enumerate (`io_<id>`, see invariant 5).
 * A union here would be false comfort; `getChannel()` returning `undefined` is the
 * honest contract, and `avl-map.test.ts` proves every mapped channel resolves.
 */
export const CHANNEL_CATEGORIES = [
    'motion',
    'engine',
    'fuel',
    'electrical',
    'temperature',
    'driver',
    'network',
    'device',
    'event',
    'geofence',
];
/**
 * A closed unit vocabulary. The UI formats from this, so a new unit is a deliberate
 * decision about how it is rendered and converted, never an ad-hoc string.
 */
export const CHANNEL_UNITS = [
    'none',
    'logic',
    'percent',
    'kph',
    'metre',
    'kilometre',
    'litre',
    'litre_per_hour',
    'rpm',
    'celsius',
    'volt',
    'ampere',
    'second',
    'minute',
    'hour',
    'millisecond',
    'g_force',
    'count',
    'bars',
    'bitmask',
    'identifier',
    'text',
    'dimensionless',
    'kilogram',
    'kwh_per_100km',
];
/**
 * How a channel collapses over a time bucket. `last` for states and levels, `sum`
 * for per-interval quantities, `avg` for measurements, `max` for monotonic counters
 * and for the peaks a fleet report is actually about (top speed, not mean speed).
 */
export const CHANNEL_AGGREGATIONS = ['last', 'sum', 'avg', 'max'];
/**
 * - `always`   — the FMC150 emits it with no extra hardware.
 * - `can`      — needs an LV-CAN200 / ALL-CAN300 / CANCONTROL adapter on the bus.
 * - `optional` — needs a peripheral (1-Wire probe, RFID reader, BLE tag) or a
 *                firmware feature that may not be configured.
 * - `derived`  — computed by RIVENA, never present in a record.
 */
export const CHANNEL_AVAILABILITIES = ['always', 'can', 'optional', 'derived'];
export const CHANNEL_VALUE_TYPES = [
    'number',
    'boolean',
    'enum',
    'bitmask',
    'opaque',
    'text',
];
/**
 * Geofence zones are 50 identical channels differing only by ordinal, so they are
 * expanded from one row rather than transcribed 50 times. The AVL ids behind them are
 * scattered across the id space (61–65, 70, 88, 91–99, …) and live in `avl-map.ts`;
 * the registry only needs to know the zones exist.
 */
export const GEOFENCE_ZONE_COUNT = 50;
export function geofenceZoneChannelKey(zone) {
    return `geofence_zone_${String(zone).padStart(2, '0')}`;
}
const GEOFENCE_ZONE_ENUM = {
    0: { fr: 'Sortie de zone', en: 'Left zone' },
    1: { fr: 'Entrée en zone', en: 'Entered zone' },
    2: { fr: 'Fin de survitesse en zone', en: 'Zone over-speeding ended' },
    3: { fr: 'Début de survitesse en zone', en: 'Zone over-speeding started' },
};
function geofenceZoneChannels() {
    const rows = [];
    for (let zone = 1; zone <= GEOFENCE_ZONE_COUNT; zone += 1) {
        const ordinal = String(zone).padStart(2, '0');
        rows.push({
            key: geofenceZoneChannelKey(zone),
            unit: 'none',
            labelFr: `Zone géorepérage ${ordinal}`,
            labelEn: `Geofence zone ${ordinal}`,
            category: 'geofence',
            aggregation: 'last',
            precision: 0,
            availability: 'always',
            showInRawPanel: true,
            valueType: 'enum',
            enumLabels: GEOFENCE_ZONE_ENUM,
        });
    }
    return rows;
}
/**
 * A raised warning lamp aggregates as `max`, never `last`: a bucket that ends with the
 * lamp off must still report that it was on, or an hourly rollup hides every
 * intermittent fault — which is the only kind a driver ever reports.
 */
const SSF_ROWS = [
    [652, 'ssf_key_in_ignition_lock', 'Clé dans le barillet', 'Key in ignition lock'],
    [653, 'ssf_handbrake', 'Frein à main serré', 'Handbrake active'],
    [654, 'ssf_door_front_left', 'Porte avant gauche ouverte', 'Front left door open'],
    [655, 'ssf_door_front_right', 'Porte avant droite ouverte', 'Front right door open'],
    [656, 'ssf_door_rear_left', 'Porte arrière gauche ouverte', 'Rear left door open'],
    [657, 'ssf_door_rear_right', 'Porte arrière droite ouverte', 'Rear right door open'],
    [658, 'ssf_door_trunk', 'Coffre ouvert', 'Trunk open'],
    [659, 'ssf_gear_neutral', 'Point mort (boîte automatique)', 'Neutral gear (automatic)'],
    [660, 'ssf_gear_parking', 'Position parking (boîte automatique)', 'Parking gear (automatic)'],
    [661, 'ssf_gear_reverse', 'Marche arrière engagée', 'Reverse gear active'],
    [662, 'ssf_car_closed', 'Véhicule verrouillé', 'Car is closed'],
    [898, 'ssf_ignition', 'Contact (CAN)', 'Ignition (CAN)', 'engine'],
    [899, 'ssf_webasto', 'Chauffage Webasto', 'Webasto heater'],
    [900, 'ssf_engine_working', 'Moteur en fonctionnement', 'Engine working', 'engine'],
    [901, 'ssf_standalone_engine', 'Moteur autonome', 'Standalone engine', 'engine'],
    [902, 'ssf_ready_to_drive', 'Prêt à rouler', 'Ready to drive', 'engine'],
    [903, 'ssf_engine_on_cng', 'Moteur au GNC', 'Engine working on CNG', 'engine'],
    [904, 'ssf_work_mode', 'Mode travail', 'Work mode'],
    [905, 'ssf_operator', 'Opérateur présent', 'Operator present'],
    [906, 'ssf_interlock', 'Verrouillage de sécurité', 'Interlock'],
    [907, 'ssf_engine_lock_active', 'Blocage moteur actif', 'Engine lock active', 'engine'],
    [908, 'ssf_engine_lock_requested', 'Blocage moteur demandé', 'Request to lock engine', 'engine'],
    [909, 'ssf_roof_open', 'Toit ouvert', 'Roof open'],
    [910, 'ssf_footbrake', 'Frein de service actionné', 'Footbrake is active'],
    [911, 'ssf_clutch_pushed', 'Embrayage enfoncé', 'Clutch pushed'],
    [912, 'ssf_hazard_lights', 'Feux de détresse', 'Hazard warning lights'],
    [913, 'ssf_engine_cover_open', 'Capot moteur ouvert', 'Engine cover open'],
    [914, 'ssf_charging_wire_plugged', 'Câble de charge branché', 'Charging wire plugged', 'electrical'], // prettier-ignore
    [915, 'ssf_battery_charging', 'Batterie de traction en charge', 'Battery charging', 'electrical'],
    [916, 'ssf_electric_engine_state', 'Moteur électrique actif', 'Electric engine state', 'engine'],
    [917, 'ssf_closed_factory_remote', 'Verrouillé par télécommande d’origine', 'Closed by factory remote'], // prettier-ignore
    [918, 'ssf_factory_alarm_actuated', 'Alarme d’origine déclenchée', 'Factory alarm actuated', 'event', 'max'], // prettier-ignore
    [919, 'ssf_factory_alarm_emulated', 'Alarme d’origine émulée', 'Factory alarm emulated', 'event', 'max'], // prettier-ignore
    [920, 'ssf_signal_close_factory_remote', 'Signal de verrouillage (télécommande d’origine)', 'Close signal from factory remote'], // prettier-ignore
    [921, 'ssf_signal_open_factory_remote', 'Signal de déverrouillage (télécommande d’origine)', 'Open signal from factory remote'], // prettier-ignore
    [922, 'ssf_rearming_signal', 'Signal de réarmement', 'Rearming signal'],
    [923, 'ssf_trunk_opened_factory_remote', 'Coffre ouvert par télécommande d’origine', 'Trunk opened by factory remote'], // prettier-ignore
    [924, 'ssf_can_module_sleep', 'Module CAN en veille', 'CAN module in sleep'],
    [925, 'ssf_factory_remote_3x', 'Télécommande d’origine pressée 3 fois', 'Factory remote pressed 3×'], // prettier-ignore
    [926, 'ssf_factory_armed', 'Alarme d’origine armée', 'Factory alarm armed'],
    [927, 'ssf_gear_drive', 'Position Drive (boîte automatique)', 'Drive gear active (automatic)'],
    [1083, 'ssf_engine_on_dual_fuel', 'Moteur en bicarburation', 'Engine working on dual fuel', 'engine'], // prettier-ignore
    [1084, 'ssf_engine_on_lpg', 'Moteur au GPL', 'Engine working on LPG', 'engine'],
];
const CSF_ROWS = [
    [928, 'csf_parking_lights', 'Feux de position', 'Parking lights'],
    [929, 'csf_dipped_headlights', 'Feux de croisement', 'Dipped headlights'],
    [930, 'csf_full_beam_headlights', 'Feux de route', 'Full beam headlights'],
    [931, 'csf_rear_fog_lights', 'Feux de brouillard arrière', 'Rear fog lights'],
    [932, 'csf_front_fog_lights', 'Feux de brouillard avant', 'Front fog lights'],
    [933, 'csf_additional_front_lights', 'Feux additionnels avant', 'Additional front lights'],
    [934, 'csf_additional_rear_lights', 'Feux additionnels arrière', 'Additional rear lights'],
    [935, 'csf_light_signal', 'Appel de phares', 'Light signal'],
    [936, 'csf_air_conditioning', 'Climatisation', 'Air conditioning'],
    [937, 'csf_cruise_control', 'Régulateur de vitesse', 'Cruise control'],
    [938, 'csf_automatic_retarder', 'Ralentisseur automatique', 'Automatic retarder'],
    [939, 'csf_manual_retarder', 'Ralentisseur manuel', 'Manual retarder'],
    [940, 'csf_seatbelt_driver', 'Ceinture conducteur bouclée', 'Driver’s seatbelt fastened'],
    [941, 'csf_seatbelt_front_driver', 'Ceinture conducteur avant bouclée', 'Front driver’s seatbelt fastened'], // prettier-ignore
    [942, 'csf_seatbelt_left_driver', 'Ceinture conducteur gauche bouclée', 'Left driver’s seatbelt fastened'], // prettier-ignore
    [943, 'csf_seatbelt_right_driver', 'Ceinture conducteur droite bouclée', 'Right driver’s seatbelt fastened'], // prettier-ignore
    [944, 'csf_seatbelt_centre_driver', 'Ceinture conducteur centrale bouclée', 'Centre driver’s seatbelt fastened'], // prettier-ignore
    [945, 'csf_front_passenger_present', 'Passager avant présent', 'Front passenger present'],
    [946, 'csf_pto', 'Prise de force (PDF)', 'Power take-off (PTO)'],
    [947, 'csf_front_differential_locked', 'Différentiel avant bloqué', 'Front differential locked'],
    [948, 'csf_rear_differential_locked', 'Différentiel arrière bloqué', 'Rear differential locked'],
    [949, 'csf_central_differential_4hi_locked', 'Différentiel central 4HI bloqué', 'Central differential 4HI locked'], // prettier-ignore
    [950, 'csf_rear_differential_4lo_locked', 'Différentiel arrière 4LO bloqué', 'Rear differential 4LO locked'], // prettier-ignore
    [951, 'csf_trailer_axle_1_lift', 'Relevage essieu 1 de remorque', 'Trailer axle 1 lift active'],
    [952, 'csf_trailer_axle_2_lift', 'Relevage essieu 2 de remorque', 'Trailer axle 2 lift active'],
    [1085, 'csf_trailer_connected', 'Remorque attelée', 'Trailer connected'],
    [1086, 'csf_start_stop_inactive', 'Système Start-Stop inactif', 'Start-stop system inactive'],
];
const ISF_ROWS = [
    [953, 'isf_check_engine', 'Témoin moteur', 'Check engine indicator'],
    [954, 'isf_abs', 'Témoin ABS', 'ABS indicator'],
    [955, 'isf_esp', 'Témoin ESP', 'ESP indicator'],
    [956, 'isf_esp_off', 'ESP désactivé', 'ESP turned off'],
    [957, 'isf_stop', 'Témoin STOP', 'Stop indicator'],
    [958, 'isf_oil_level', 'Témoin niveau d’huile', 'Oil level indicator'],
    [959, 'isf_coolant_level', 'Témoin niveau de liquide de refroidissement', 'Coolant liquid level indicator'], // prettier-ignore
    [960, 'isf_battery_not_charging', 'Témoin batterie non chargée', 'Battery not charging indicator'], // prettier-ignore
    [961, 'isf_handbrake_system', 'Témoin circuit de frein à main', 'Handbrake system indicator'],
    [962, 'isf_airbag', 'Témoin airbag', 'Airbag indicator'],
    [963, 'isf_eps', 'Témoin EPS (direction assistée)', 'EPS indicator'],
    [964, 'isf_warning', 'Témoin d’avertissement', 'Warning indicator'],
    [965, 'isf_lights_failure', 'Témoin défaut d’éclairage', 'Lights failure indicator'],
    [966, 'isf_low_tyre_pressure', 'Témoin pression pneus basse', 'Low tyre pressure indicator'],
    [967, 'isf_brake_pad_wear', 'Témoin usure des plaquettes', 'Wear of brake pads indicator'],
    [968, 'isf_low_fuel_level', 'Témoin réserve de carburant', 'Low fuel level indicator', 'fuel'],
    [969, 'isf_maintenance_required', 'Témoin entretien requis', 'Maintenance required indicator'],
    [970, 'isf_glow_plug', 'Témoin de préchauffage', 'Glow plug indicator'],
    [971, 'isf_fap', 'Témoin FAP (filtre à particules)', 'FAP (particulate filter) indicator'],
    [972, 'isf_epc', 'Témoin EPC', 'EPC (electronic power control) indicator'],
    [973, 'isf_clogged_oil_filter', 'Témoin filtre à huile colmaté', 'Clogged engine oil filter indicator'], // prettier-ignore
    [974, 'isf_low_oil_pressure', 'Témoin pression d’huile basse', 'Low engine oil pressure indicator'], // prettier-ignore
    [975, 'isf_high_oil_temperature', 'Témoin température d’huile élevée', 'Too high engine oil temperature indicator'], // prettier-ignore
    [976, 'isf_low_coolant_level', 'Témoin niveau de refroidissement bas', 'Low coolant level indicator'], // prettier-ignore
    [977, 'isf_clogged_hydraulic_oil_filter', 'Témoin filtre à huile hydraulique colmaté', 'Clogged hydraulic system oil filter indicator'], // prettier-ignore
    [978, 'isf_hydraulic_low_pressure', 'Témoin pression hydraulique basse', 'Hydraulic system low pressure indicator'], // prettier-ignore
    [979, 'isf_hydraulic_low_level', 'Témoin niveau d’huile hydraulique bas', 'Hydraulic oil low level indicator'], // prettier-ignore
    [980, 'isf_hydraulic_high_temperature', 'Témoin température hydraulique élevée', 'Hydraulic system high temperature indicator'], // prettier-ignore
    [981, 'isf_hydraulic_chamber_overflow', 'Témoin débordement d’huile en chambre hydraulique', 'Oil overflow in hydraulic chamber indicator'], // prettier-ignore
    [982, 'isf_clogged_air_filter', 'Témoin filtre à air colmaté', 'Clogged air filter indicator'],
    [983, 'isf_clogged_fuel_filter', 'Témoin filtre à carburant colmaté', 'Clogged fuel filter indicator', 'fuel'], // prettier-ignore
    [984, 'isf_water_in_fuel', 'Témoin eau dans le carburant', 'Water in fuel indicator', 'fuel'],
    [985, 'isf_clogged_brake_filter', 'Témoin filtre de circuit de frein colmaté', 'Clogged brake system filter indicator'], // prettier-ignore
    [986, 'isf_low_washer_fluid', 'Témoin niveau de lave-glace bas', 'Low washer fluid level indicator'], // prettier-ignore
    [987, 'isf_low_adblue', 'Témoin niveau AdBlue bas', 'Low AdBlue level indicator', 'fuel'],
    [988, 'isf_low_trailer_tyre_pressure', 'Témoin pression pneus remorque basse', 'Low trailer tyre pressure indicator'], // prettier-ignore
    [989, 'isf_trailer_brake_lining_wear', 'Témoin usure garnitures de frein remorque', 'Wear of trailer brake lining indicator'], // prettier-ignore
    [990, 'isf_trailer_brake_high_temperature', 'Témoin température de frein remorque élevée', 'High trailer brake temperature indicator'], // prettier-ignore
    [991, 'isf_trailer_pneumatic_supply', 'Témoin alimentation pneumatique remorque incorrecte', 'Incorrect trailer pneumatic supply indicator'], // prettier-ignore
    [992, 'isf_low_cng_level', 'Témoin niveau GNC bas', 'Low CNG level indicator', 'fuel'],
];
const ASF_ROWS = [
    [993, 'asf_right_joystick_right', 'Joystick droit vers la droite', 'Right joystick moved right'],
    [994, 'asf_right_joystick_left', 'Joystick droit vers la gauche', 'Right joystick moved left'],
    [995, 'asf_right_joystick_forward', 'Joystick droit vers l’avant', 'Right joystick moved forward'], // prettier-ignore
    [996, 'asf_right_joystick_back', 'Joystick droit vers l’arrière', 'Right joystick moved back'],
    [997, 'asf_left_joystick_right', 'Joystick gauche vers la droite', 'Left joystick moved right'],
    [998, 'asf_left_joystick_left', 'Joystick gauche vers la gauche', 'Left joystick moved left'],
    [999, 'asf_left_joystick_forward', 'Joystick gauche vers l’avant', 'Left joystick moved forward'],
    [1000, 'asf_left_joystick_back', 'Joystick gauche vers l’arrière', 'Left joystick moved back'],
    [1001, 'asf_rear_hydraulic_1', '1er distributeur hydraulique arrière', 'First rear hydraulic'],
    [1002, 'asf_rear_hydraulic_2', '2e distributeur hydraulique arrière', 'Second rear hydraulic'],
    [1003, 'asf_rear_hydraulic_3', '3e distributeur hydraulique arrière', 'Third rear hydraulic'],
    [1004, 'asf_rear_hydraulic_4', '4e distributeur hydraulique arrière', 'Fourth rear hydraulic'],
    [1005, 'asf_front_hydraulic_1', '1er distributeur hydraulique avant', 'First front hydraulic'],
    [1006, 'asf_front_hydraulic_2', '2e distributeur hydraulique avant', 'Second front hydraulic'],
    [1007, 'asf_front_hydraulic_3', '3e distributeur hydraulique avant', 'Third front hydraulic'],
    [1008, 'asf_front_hydraulic_4', '4e distributeur hydraulique avant', 'Fourth front hydraulic'],
    [1009, 'asf_front_three_point_hitch', 'Relevage 3 points avant', 'Front three-point hitch'],
    [1010, 'asf_rear_three_point_hitch', 'Relevage 3 points arrière', 'Rear three-point hitch'],
    [1011, 'asf_front_pto', 'Prise de force avant', 'Front power take-off'],
    [1012, 'asf_rear_pto', 'Prise de force arrière', 'Rear power take-off'],
    [1013, 'asf_mowing', 'Fauchage actif', 'Mowing active'],
    [1014, 'asf_threshing', 'Battage actif', 'Threshing active'],
    [1015, 'asf_grain_release', 'Vidange de la trémie', 'Grain release from hopper'],
    [1016, 'asf_grain_tank_full', 'Trémie pleine à 100 %', 'Grain tank is 100 % full'],
    [1017, 'asf_grain_tank_70', 'Trémie pleine à 70 %', 'Grain tank is 70 % full'],
    [1018, 'asf_grain_tank_open', 'Trémie ouverte', 'Grain tank is opened'],
    [1019, 'asf_unloader_drive', 'Entraînement de la vis de déchargement', 'Unloader drive'],
    [1020, 'asf_cleaning_fan_off', 'Régulation du ventilateur de nettoyage coupée', 'Cleaning fan control turned off'], // prettier-ignore
    [1021, 'asf_threshing_drum_off', 'Régulation du batteur coupée', 'Threshing drum control turned off'], // prettier-ignore
    [1022, 'asf_straw_walker_clogged', 'Secoueur de paille colmaté', 'Straw walker is clogged'],
    [1023, 'asf_threshing_drum_clearance', 'Jeu excessif sous le batteur', 'Excessive clearance under the threshing drum'], // prettier-ignore
    [1024, 'asf_hydraulics_low_temperature', 'Hydraulique de transmission sous 5 °C', 'Drive-system hydraulics below 5 °C'], // prettier-ignore
    [1025, 'asf_hydraulics_high_temperature', 'Hydraulique de transmission au-dessus de 86 °C', 'Drive-system hydraulics above 86 °C'], // prettier-ignore
    [1026, 'asf_ear_auger_speed_low', 'Régime de la vis à épis sous la norme', 'Ear auger speed below the norm'], // prettier-ignore
    [1027, 'asf_grain_auger_speed_low', 'Régime de la vis à grain sous la norme', 'Grain auger speed below the norm'], // prettier-ignore
    [1028, 'asf_straw_chopper_speed_low', 'Régime du broyeur de paille sous la norme', 'Straw chopper speed below the norm'], // prettier-ignore
    [1029, 'asf_straw_shaker_speed_low', 'Régime du secoueur sous la norme', 'Straw shaker speed below the norm'], // prettier-ignore
    [
        1030,
        'asf_feeder_speed_low',
        'Régime du convoyeur sous la norme',
        'Feeder speed below the norm',
    ],
    [1031, 'asf_straw_chopper_on', 'Broyeur de paille en marche', 'Straw chopper switched on'],
    [1032, 'asf_corn_header_connected', 'Cueilleur à maïs attelé', 'Corn header connected'],
    [1033, 'asf_grain_header_connected', 'Coupe à grain attelée', 'Grain header connected'],
    [1034, 'asf_feeder_reverse_on', 'Inverseur de convoyeur enclenché', 'Feeder reverse switched on'],
    [1035, 'asf_hydraulic_pump_filter_clogged', 'Filtre de refoulement de la pompe hydraulique colmaté', 'Pressure filter of the hydraulic pump is clogged'], // prettier-ignore
    [1087, 'asf_adapter_pressure_filter_sensor', 'Capteur de filtre de pression (adaptateur)', 'Adapter pressure filter sensor'], // prettier-ignore
    [1088, 'asf_service_2_required', 'Entretien 2 requis', 'Service 2 required indicator'],
    [1089, 'asf_drain_filter_clogged', 'Filtre de retour colmaté', 'Drain filter clogged indicator'],
];
const USF_ROWS = [
    [1036, 'usf_spreading', 'Épandage', 'Spreading'],
    [1037, 'usf_pouring_chemicals', 'Épandage de produits chimiques', 'Pouring chemicals'],
    [1038, 'usf_conveyor_belt', 'Tapis convoyeur', 'Conveyor belt'],
    [1039, 'usf_salt_spreader_drive_wheel', 'Roue d’entraînement de la saleuse', 'Salt spreader’s drive wheel'], // prettier-ignore
    [1040, 'usf_brushes', 'Brosses', 'Brushes'],
    [1041, 'usf_vacuum_cleaner', 'Aspirateur', 'Vacuum cleaner'],
    [1042, 'usf_water_supply', 'Alimentation en eau', 'Water supply'],
    [1043, 'usf_spreading_second_channel', 'Épandage (2e voie)', 'Spreading (second channel)'],
    [1044, 'usf_liquid_pump', 'Pompe à liquide', 'Liquid pump'],
    [1045, 'usf_unloading_from_hopper', 'Déchargement de la trémie', 'Unloading from the hopper'],
    [1046, 'usf_low_salt_level', 'Niveau de sel (sable) bas', 'Low salt (sand) level in container'],
    [1047, 'usf_low_water_level', 'Niveau d’eau bas', 'Low water level in container'],
    [1048, 'usf_chemicals', 'Produits chimiques', 'Chemicals'],
    [1049, 'usf_compressor', 'Compresseur', 'Compressor'],
    [1050, 'usf_water_valve_open', 'Vanne d’eau ouverte', 'Water valve is opened'],
    [1051, 'usf_cabin_moved_up', 'Cabine relevée', 'Cabin moved up'],
    [1052, 'usf_cabin_moved_down', 'Cabine abaissée', 'Cabin moved down'],
    [1099, 'usf_hydraulics_not_permitted', 'Travail hydraulique non autorisé', 'Hydraulics work not permitted'], // prettier-ignore
];
/** ASF spraying sections 1–9 (ids 1090–1098) and the cistern block below are regular. */
function asfSprayingRows() {
    const rows = [];
    for (let section = 1; section <= 9; section += 1) {
        rows.push([
            1089 + section,
            `asf_section_${section}_spraying`,
            `Pulvérisation tronçon ${section}`,
            `Section ${section} spraying`,
        ]);
    }
    return rows;
}
/** CiSF ids 1053–1076: eight cistern sections × three signals, in that order. */
function cisfRows() {
    const rows = [];
    for (let section = 1; section <= 8; section += 1) {
        const base = 1053 + (section - 1) * 3;
        rows.push([
            base,
            `cisf_section_${section}_fluid_in_downpipe`,
            `Compartiment ${section} — fluide dans la descente`,
            `Section ${section} — fluid in the downpipe`,
        ]);
        rows.push([
            base + 1,
            `cisf_section_${section}_filled`,
            `Compartiment ${section} — rempli`,
            `Section ${section} — filled`,
        ]);
        rows.push([
            base + 2,
            `cisf_section_${section}_overfilled`,
            `Compartiment ${section} — trop-plein`,
            `Section ${section} — overfilled`,
        ]);
    }
    return rows;
}
export const CAN_STATE_FLAGS = [
    ...SSF_ROWS,
    ...CSF_ROWS,
    // Indicators are warning lamps: they aggregate as `max` so a bucket that ends with
    // the lamp off still reports that it was on.
    ...ISF_ROWS.map((row) => [row[0], row[1], row[2], row[3], row[4] ?? 'event', 'max']), // prettier-ignore
    ...ASF_ROWS,
    ...asfSprayingRows(),
    ...USF_ROWS,
    ...cisfRows(),
].map(([avlId, key, labelFr, labelEn, category, aggregation]) => ({
    avlId,
    key,
    labelFr,
    labelEn,
    category: category ?? 'device',
    aggregation: aggregation ?? 'last',
}));
function canStateFlagChannels() {
    return CAN_STATE_FLAGS.map((flag) => ({
        key: flag.key,
        unit: 'logic',
        labelFr: flag.labelFr,
        labelEn: flag.labelEn,
        category: flag.category,
        aggregation: flag.aggregation,
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'boolean',
    }));
}
const CORE_CHANNELS = [
    // ── Motion ────────────────────────────────────────────────────────────────
    {
        key: 'speed_gps',
        unit: 'kph',
        labelFr: 'Vitesse GPS',
        labelEn: 'GPS speed',
        category: 'motion',
        aggregation: 'max',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 400 },
    },
    {
        key: 'vehicle_speed_can',
        unit: 'kph',
        labelFr: 'Vitesse véhicule (CAN)',
        labelEn: 'Vehicle speed (CAN)',
        category: 'motion',
        aggregation: 'max',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 300 },
    },
    {
        key: 'movement',
        unit: 'logic',
        labelFr: 'Mouvement',
        labelEn: 'Movement',
        category: 'motion',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'total_odometer',
        unit: 'metre',
        labelFr: 'Odomètre total (GNSS)',
        labelEn: 'Total odometer (GNSS)',
        category: 'motion',
        aggregation: 'max',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'trip_odometer',
        unit: 'metre',
        labelFr: 'Odomètre de trajet',
        labelEn: 'Trip odometer',
        category: 'motion',
        aggregation: 'max',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'total_mileage_can',
        unit: 'metre',
        labelFr: 'Kilométrage total (CAN)',
        labelEn: 'Total mileage (CAN)',
        category: 'motion',
        aggregation: 'max',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'distance_km',
        unit: 'kilometre',
        labelFr: 'Distance parcourue',
        labelEn: 'Distance travelled',
        category: 'motion',
        aggregation: 'sum',
        precision: 2,
        availability: 'derived',
        showInRawPanel: false,
        valueType: 'number',
    },
    {
        key: 'movement_state',
        unit: 'none',
        labelFr: 'État de mouvement',
        labelEn: 'Movement state',
        category: 'motion',
        aggregation: 'last',
        precision: 0,
        availability: 'derived',
        showInRawPanel: false,
        valueType: 'enum',
        enumLabels: {
            0: { fr: 'À l’arrêt', en: 'Parked' },
            1: { fr: 'Au ralenti', en: 'Idling' },
            2: { fr: 'En mouvement', en: 'Moving' },
        },
    },
    // ── Engine ────────────────────────────────────────────────────────────────
    {
        key: 'ignition',
        unit: 'logic',
        labelFr: 'Contact',
        labelEn: 'Ignition',
        category: 'engine',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'engine_rpm',
        unit: 'rpm',
        labelFr: 'Régime moteur',
        labelEn: 'Engine RPM',
        category: 'engine',
        aggregation: 'avg',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 16_000 },
    },
    {
        key: 'engine_load',
        unit: 'percent',
        labelFr: 'Charge moteur',
        labelEn: 'Engine load',
        category: 'engine',
        aggregation: 'avg',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 102 },
    },
    {
        key: 'accelerator_pedal',
        unit: 'percent',
        labelFr: 'Position pédale accélérateur',
        labelEn: 'Accelerator pedal position',
        category: 'engine',
        aggregation: 'avg',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        // 102, not 100: the vendor table's own maximum. Clamping at 100 would flag
        // every full-throttle sample on a healthy vehicle.
        range: { min: 0, max: 102 },
    },
    {
        key: 'engine_worktime',
        unit: 'minute',
        labelFr: 'Temps de fonctionnement moteur',
        labelEn: 'Engine worktime',
        category: 'engine',
        aggregation: 'max',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'engine_worktime_counted',
        unit: 'minute',
        labelFr: 'Temps moteur (compteur adaptateur)',
        labelEn: 'Engine worktime (counted)',
        category: 'engine',
        aggregation: 'max',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'engine_hours',
        unit: 'hour',
        labelFr: 'Heures moteur',
        labelEn: 'Engine hours',
        category: 'engine',
        aggregation: 'max',
        precision: 1,
        availability: 'derived',
        showInRawPanel: false,
        valueType: 'number',
    },
    {
        key: 'idling',
        unit: 'logic',
        labelFr: 'Ralenti',
        labelEn: 'Idling',
        category: 'engine',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    // ── Fuel ──────────────────────────────────────────────────────────────────
    {
        key: 'fuel_level_pct',
        unit: 'percent',
        labelFr: 'Niveau de carburant',
        labelEn: 'Fuel level',
        category: 'fuel',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 100 },
    },
    {
        key: 'fuel_level_l',
        unit: 'litre',
        labelFr: 'Niveau de carburant (litres)',
        labelEn: 'Fuel level (litres)',
        category: 'fuel',
        aggregation: 'last',
        precision: 1,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'fuel_level_l_estimated',
        unit: 'litre',
        labelFr: 'Niveau de carburant estimé (litres)',
        labelEn: 'Estimated fuel level (litres)',
        category: 'fuel',
        aggregation: 'last',
        precision: 1,
        // Kept separate from fuel_level_l on purpose. The estimate's error is the
        // configured tank capacity, not the sensor, so a theft alert has to know which
        // of the two it is looking at before it accuses anyone.
        availability: 'derived',
        showInRawPanel: false,
        valueType: 'number',
    },
    {
        key: 'fuel_rate',
        unit: 'litre_per_hour',
        labelFr: 'Consommation instantanée',
        labelEn: 'Fuel rate',
        category: 'fuel',
        aggregation: 'avg',
        precision: 1,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 600 },
    },
    {
        key: 'fuel_consumed_total',
        unit: 'litre',
        labelFr: 'Carburant consommé (total)',
        labelEn: 'Fuel consumed (total)',
        category: 'fuel',
        aggregation: 'max',
        precision: 1,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'fuel_consumed_counted',
        unit: 'litre',
        labelFr: 'Carburant consommé (compteur adaptateur)',
        labelEn: 'Fuel consumed (counted)',
        category: 'fuel',
        aggregation: 'max',
        precision: 1,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'fuel_used_gps',
        unit: 'litre',
        labelFr: 'Carburant consommé (estimé GPS)',
        labelEn: 'Fuel used (GPS estimate)',
        category: 'fuel',
        aggregation: 'max',
        precision: 3,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
    },
    // ── Temperature ───────────────────────────────────────────────────────────
    {
        key: 'coolant_temperature',
        unit: 'celsius',
        labelFr: 'Température liquide de refroidissement',
        labelEn: 'Coolant temperature',
        category: 'temperature',
        aggregation: 'avg',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: -60, max: 200 },
    },
    {
        key: 'engine_temperature',
        unit: 'celsius',
        labelFr: 'Température moteur',
        labelEn: 'Engine temperature',
        category: 'temperature',
        aggregation: 'avg',
        precision: 1,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: -60, max: 200 },
    },
    {
        key: 'ambient_air_temperature',
        unit: 'celsius',
        labelFr: 'Température air ambiant',
        labelEn: 'Ambient air temperature',
        category: 'temperature',
        aggregation: 'avg',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: -60, max: 90 },
    },
    {
        key: 'dallas_temperature_1',
        unit: 'celsius',
        labelFr: 'Sonde Dallas 1',
        labelEn: 'Dallas probe 1',
        category: 'temperature',
        aggregation: 'avg',
        precision: 1,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: -60, max: 150 },
    },
    {
        key: 'dallas_temperature_2',
        unit: 'celsius',
        labelFr: 'Sonde Dallas 2',
        labelEn: 'Dallas probe 2',
        category: 'temperature',
        aggregation: 'avg',
        precision: 1,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: -60, max: 150 },
    },
    {
        key: 'dallas_temperature_3',
        unit: 'celsius',
        labelFr: 'Sonde Dallas 3',
        labelEn: 'Dallas probe 3',
        category: 'temperature',
        aggregation: 'avg',
        precision: 1,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: -60, max: 150 },
    },
    {
        key: 'dallas_temperature_4',
        unit: 'celsius',
        labelFr: 'Sonde Dallas 4',
        labelEn: 'Dallas probe 4',
        category: 'temperature',
        aggregation: 'avg',
        precision: 1,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: -60, max: 150 },
    },
    {
        key: 'dallas_id_1',
        unit: 'identifier',
        labelFr: 'Identifiant sonde Dallas 1',
        labelEn: 'Dallas probe 1 ID',
        category: 'temperature',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'opaque',
    },
    {
        key: 'dallas_id_2',
        unit: 'identifier',
        labelFr: 'Identifiant sonde Dallas 2',
        labelEn: 'Dallas probe 2 ID',
        category: 'temperature',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'opaque',
    },
    {
        key: 'dallas_id_3',
        unit: 'identifier',
        labelFr: 'Identifiant sonde Dallas 3',
        labelEn: 'Dallas probe 3 ID',
        category: 'temperature',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'opaque',
    },
    {
        key: 'dallas_id_4',
        unit: 'identifier',
        labelFr: 'Identifiant sonde Dallas 4',
        labelEn: 'Dallas probe 4 ID',
        category: 'temperature',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'opaque',
    },
    // ── Electrical ────────────────────────────────────────────────────────────
    {
        key: 'external_voltage',
        unit: 'volt',
        labelFr: 'Tension externe',
        labelEn: 'External voltage',
        category: 'electrical',
        aggregation: 'avg',
        precision: 2,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
        // 24 V fleets peak near 30 V while charging; 60 V catches a mis-mapped element
        // without flagging a healthy truck.
        range: { min: 0, max: 60 },
    },
    {
        key: 'battery_voltage',
        unit: 'volt',
        labelFr: 'Tension batterie interne',
        labelEn: 'Internal battery voltage',
        category: 'electrical',
        aggregation: 'avg',
        precision: 2,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 16 },
    },
    {
        key: 'battery_current',
        unit: 'ampere',
        labelFr: 'Courant batterie interne',
        labelEn: 'Internal battery current',
        category: 'electrical',
        aggregation: 'avg',
        precision: 3,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 50 },
    },
    {
        key: 'battery_level',
        unit: 'percent',
        labelFr: 'Charge batterie interne',
        labelEn: 'Internal battery level',
        category: 'electrical',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 100 },
    },
    {
        key: 'control_module_voltage',
        unit: 'volt',
        labelFr: 'Tension calculateur',
        labelEn: 'Control module voltage',
        category: 'electrical',
        aggregation: 'avg',
        precision: 2,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 60 },
    },
    {
        key: 'analog_input_1',
        unit: 'volt',
        labelFr: 'Entrée analogique 1',
        labelEn: 'Analog input 1',
        category: 'electrical',
        aggregation: 'avg',
        precision: 3,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 40 },
    },
    // ── Device I/O and status ─────────────────────────────────────────────────
    {
        key: 'digital_input_1',
        unit: 'logic',
        labelFr: 'Entrée numérique 1',
        labelEn: 'Digital input 1',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'digital_input_2',
        unit: 'logic',
        labelFr: 'Entrée numérique 2',
        labelEn: 'Digital input 2',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'digital_input_3',
        unit: 'logic',
        labelFr: 'Entrée numérique 3',
        labelEn: 'Digital input 3',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'digital_output_1',
        unit: 'logic',
        labelFr: 'Sortie numérique 1',
        labelEn: 'Digital output 1',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'digital_output_2',
        unit: 'logic',
        labelFr: 'Sortie numérique 2',
        labelEn: 'Digital output 2',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'pulse_counter_din1',
        unit: 'count',
        labelFr: 'Compteur d’impulsions DIN1',
        labelEn: 'DIN1 pulse counter',
        category: 'device',
        aggregation: 'max',
        precision: 0,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'gnss_status',
        unit: 'none',
        labelFr: 'État GNSS',
        labelEn: 'GNSS status',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'enum',
        enumLabels: {
            0: { fr: 'GNSS éteint', en: 'GNSS off' },
            1: { fr: 'GNSS actif, position fixée', en: 'GNSS on, fix acquired' },
            2: { fr: 'GNSS actif, sans position', en: 'GNSS on, no fix' },
            3: { fr: 'GNSS en veille', en: 'GNSS sleep' },
        },
    },
    {
        key: 'gnss_pdop',
        unit: 'dimensionless',
        labelFr: 'PDOP GNSS',
        labelEn: 'GNSS PDOP',
        category: 'device',
        aggregation: 'avg',
        precision: 1,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 100 },
    },
    {
        key: 'gnss_hdop',
        unit: 'dimensionless',
        labelFr: 'HDOP GNSS',
        labelEn: 'GNSS HDOP',
        category: 'device',
        aggregation: 'avg',
        precision: 1,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 100 },
    },
    {
        key: 'data_mode',
        unit: 'none',
        labelFr: 'Mode de transmission',
        labelEn: 'Data mode',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'enum',
    },
    {
        key: 'sleep_mode',
        unit: 'none',
        labelFr: 'Mode veille',
        labelEn: 'Sleep mode',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'enum',
        enumLabels: {
            0: { fr: 'Aucune veille', en: 'No sleep' },
            1: { fr: 'Veille GPS', en: 'GPS sleep' },
            2: { fr: 'Veille profonde', en: 'Deep sleep' },
            3: { fr: 'Veille en ligne', en: 'Online sleep' },
            4: { fr: 'Veille ultra profonde', en: 'Ultra deep sleep' },
        },
    },
    {
        key: 'program_number',
        unit: 'identifier',
        labelFr: 'Numéro de programme CAN',
        labelEn: 'CAN program number',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'can_module_id',
        unit: 'identifier',
        labelFr: 'Identifiant module CAN',
        labelEn: 'CAN module ID',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'opaque',
    },
    {
        key: 'door_status',
        unit: 'bitmask',
        labelFr: 'État des ouvrants',
        labelEn: 'Door status',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'bitmask',
    },
    {
        key: 'control_state_flags',
        unit: 'bitmask',
        labelFr: 'Drapeaux d’état de commande',
        labelEn: 'Control state flags',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'bitmask',
    },
    {
        key: 'security_state_flags',
        unit: 'bitmask',
        labelFr: 'Drapeaux d’état de sécurité',
        labelEn: 'Security state flags',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'bitmask',
    },
    // OBD-II readings of what the CAN channels above also measure. Separate keys on
    // purpose — see the note on ids 36/37/48 in avl-map.ts.
    {
        key: 'obd_engine_rpm',
        unit: 'rpm',
        labelFr: 'Régime moteur (OBD)',
        labelEn: 'Engine RPM (OBD)',
        category: 'engine',
        aggregation: 'avg',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 16_384 },
    },
    {
        key: 'obd_vehicle_speed',
        unit: 'kph',
        labelFr: 'Vitesse véhicule (OBD)',
        labelEn: 'Vehicle speed (OBD)',
        category: 'motion',
        aggregation: 'avg',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 255 },
    },
    {
        key: 'obd_fuel_level_pct',
        unit: 'percent',
        labelFr: 'Niveau de carburant (OBD)',
        labelEn: 'Fuel level (OBD)',
        category: 'fuel',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 100 },
    },
    {
        key: 'obd_dtc_count',
        unit: 'count',
        labelFr: 'Nombre de codes défaut',
        labelEn: 'Number of DTCs',
        category: 'device',
        aggregation: 'max',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'distance_since_codes_clear',
        unit: 'kilometre',
        labelFr: 'Distance depuis effacement des codes',
        labelEn: 'Distance since codes cleared',
        category: 'device',
        aggregation: 'max',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'gyroscope_axis',
        unit: 'none',
        labelFr: 'Axes gyroscope (brut)',
        labelEn: 'Gyroscope axes (raw)',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: true,
        // Opaque on purpose: the only source packs Z/Y/X into bytes but contradicts
        // itself on the element's width, so decomposing it here would invent numbers.
        valueType: 'opaque',
    },
    // ── Network ───────────────────────────────────────────────────────────────
    {
        key: 'gsm_signal',
        unit: 'bars',
        labelFr: 'Signal GSM',
        labelEn: 'GSM signal',
        category: 'network',
        aggregation: 'avg',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
        // Bars 0–5, not dBm. Rendering it as dBm produces a "5 dBm" that looks like a
        // catastrophic signal on a device with perfect reception.
        range: { min: 0, max: 5 },
    },
    {
        key: 'gsm_operator',
        unit: 'identifier',
        labelFr: 'Opérateur GSM actif',
        labelEn: 'Active GSM operator',
        category: 'network',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'gsm_cell_id',
        unit: 'identifier',
        labelFr: 'Identifiant de cellule GSM',
        labelEn: 'GSM cell ID',
        category: 'network',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'gsm_area_code',
        unit: 'identifier',
        labelFr: 'Code de zone GSM',
        labelEn: 'GSM area code',
        category: 'network',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'network_type',
        unit: 'none',
        labelFr: 'Type de réseau',
        labelEn: 'Network type',
        category: 'network',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'enum',
        enumLabels: {
            0: { fr: '3G', en: '3G' },
            1: { fr: 'GSM', en: 'GSM' },
            2: { fr: '4G', en: '4G' },
            3: { fr: 'LTE CAT M1', en: 'LTE CAT M1' },
            4: { fr: 'LTE CAT NB1', en: 'LTE CAT NB1' },
            99: { fr: 'Inconnu', en: 'Unknown' },
        },
    },
    {
        key: 'ble_nmea_receiver_mac',
        unit: 'identifier',
        // Teltonika calls id 238 "User ID". It is the MAC address of a Bluetooth NMEA
        // receiver, not a person. The label says so in both languages because the
        // vendor's own name is what makes fleets attribute trips to a BLE dongle.
        labelFr: 'Adresse MAC récepteur NMEA Bluetooth',
        labelEn: 'Bluetooth NMEA receiver MAC address',
        category: 'network',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'opaque',
    },
    // ── Driver identification ─────────────────────────────────────────────────
    {
        key: 'ibutton_id',
        unit: 'identifier',
        labelFr: 'Clé iButton',
        labelEn: 'iButton key',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: false,
        valueType: 'opaque',
        personalData: true,
    },
    {
        key: 'ibutton_authorized',
        unit: 'identifier',
        labelFr: 'Clé iButton autorisée',
        labelEn: 'Authorized iButton key',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: false,
        valueType: 'opaque',
        personalData: true,
    },
    {
        key: 'rfid_id',
        unit: 'identifier',
        labelFr: 'Badge RFID',
        labelEn: 'RFID tag',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: false,
        valueType: 'opaque',
        personalData: true,
    },
    {
        key: 'driver1_id_high',
        unit: 'identifier',
        labelFr: 'Carte conducteur 1 — mot haut',
        labelEn: 'Driver 1 card — high word',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: false,
        valueType: 'opaque',
        personalData: true,
    },
    {
        key: 'driver1_id_low',
        unit: 'identifier',
        labelFr: 'Carte conducteur 1 — mot bas',
        labelEn: 'Driver 1 card — low word',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: false,
        valueType: 'opaque',
        personalData: true,
    },
    {
        key: 'driver2_id_high',
        unit: 'identifier',
        labelFr: 'Carte conducteur 2 — mot haut',
        labelEn: 'Driver 2 card — high word',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: false,
        valueType: 'opaque',
        personalData: true,
    },
    {
        key: 'driver2_id_low',
        unit: 'identifier',
        labelFr: 'Carte conducteur 2 — mot bas',
        labelEn: 'Driver 2 card — low word',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: false,
        valueType: 'opaque',
        personalData: true,
    },
    {
        key: 'driver_name',
        unit: 'text',
        labelFr: 'Nom du conducteur',
        labelEn: 'Driver name',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: false,
        valueType: 'text',
        personalData: true,
    },
    {
        key: 'driver_card_license_type',
        unit: 'none',
        labelFr: 'Type de permis (carte conducteur)',
        labelEn: 'Driver card licence type',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: false,
        valueType: 'enum',
        personalData: true,
        enumLabels: {
            0: { fr: 'Aucun', en: 'None' },
            1: { fr: 'B.1', en: 'B.1' },
            2: { fr: 'B.2', en: 'B.2' },
            3: { fr: 'B.3', en: 'B.3' },
            4: { fr: 'B.4', en: 'B.4' },
            5: { fr: 'T.1', en: 'T.1' },
            6: { fr: 'T.2', en: 'T.2' },
            7: { fr: 'T.3', en: 'T.3' },
            8: { fr: 'T.4', en: 'T.4' },
        },
    },
    {
        key: 'driver_gender',
        unit: 'none',
        labelFr: 'Genre du conducteur',
        labelEn: 'Driver gender',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: false,
        valueType: 'enum',
        personalData: true,
        enumLabels: {
            0: { fr: 'Non renseigné', en: 'None' },
            1: { fr: 'Homme', en: 'Male' },
            2: { fr: 'Femme', en: 'Female' },
        },
    },
    {
        key: 'driver_status_event',
        unit: 'none',
        labelFr: 'Évènement conducteur',
        labelEn: 'Driver status event',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: false,
        valueType: 'enum',
        personalData: true,
        enumLabels: {
            0: { fr: 'Prise de service', en: 'Registered' },
            1: { fr: 'Fin de service', en: 'Deregistered' },
            2: { fr: 'Relève de conducteur', en: 'Swapping' },
        },
    },
    {
        key: 'barcode_id',
        unit: 'text',
        labelFr: 'Code-barres scanné',
        labelEn: 'Scanned barcode',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: false,
        valueType: 'text',
        personalData: true,
    },
    {
        key: 'beacon_list',
        unit: 'identifier',
        labelFr: 'Balises BLE détectées',
        labelEn: 'Detected BLE beacons',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: false,
        valueType: 'opaque',
        personalData: true,
    },
    {
        key: 'beacon_advanced',
        unit: 'identifier',
        labelFr: 'Balises BLE (mode avancé)',
        labelEn: 'BLE beacons (advanced mode)',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: false,
        valueType: 'opaque',
        personalData: true,
    },
    {
        key: 'immobilizer',
        unit: 'none',
        labelFr: 'Immobilisateur',
        labelEn: 'Immobilizer',
        category: 'driver',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'enum',
        enumLabels: {
            0: { fr: 'iButton non connecté', en: 'iButton not connected' },
            1: { fr: 'iButton connecté (immobilisateur)', en: 'iButton connected (immobilizer)' },
            2: {
                fr: 'iButton connecté (conduite autorisée)',
                en: 'iButton connected (authorized driving)',
            },
        },
    },
    // ── Events ────────────────────────────────────────────────────────────────
    {
        key: 'alarm',
        unit: 'logic',
        labelFr: 'Alarme',
        labelEn: 'Alarm',
        category: 'event',
        aggregation: 'max',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'towing',
        unit: 'logic',
        labelFr: 'Remorquage détecté',
        labelEn: 'Towing detected',
        category: 'event',
        aggregation: 'max',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'jamming',
        unit: 'logic',
        labelFr: 'Brouillage GSM',
        labelEn: 'GSM jamming',
        category: 'event',
        aggregation: 'max',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'unplug',
        unit: 'logic',
        labelFr: 'Batterie débranchée',
        labelEn: 'Battery unplugged',
        category: 'event',
        aggregation: 'max',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'crash_detection',
        unit: 'none',
        labelFr: 'Détection de choc',
        labelEn: 'Crash detection',
        category: 'event',
        aggregation: 'max',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'enum',
        enumLabels: {
            1: { fr: 'Choc réel (appareil calibré)', en: 'Real crash (device calibrated)' },
            2: { fr: 'Trace de choc limitée (non calibré)', en: 'Limited crash trace (not calibrated)' },
            3: { fr: 'Trace de choc limitée (calibré)', en: 'Limited crash trace (calibrated)' },
            4: { fr: 'Trace de choc complète (non calibré)', en: 'Full crash trace (not calibrated)' },
            5: { fr: 'Trace de choc complète (calibré)', en: 'Full crash trace (calibrated)' },
            6: { fr: 'Choc réel (non calibré)', en: 'Real crash (not calibrated)' },
            7: { fr: 'Faux choc (nid-de-poule)', en: 'Fake crash (pothole)' },
            8: { fr: 'Faux choc (contrôle de vitesse)', en: 'Fake crash (speed check)' },
        },
    },
    {
        key: 'trip_event',
        unit: 'none',
        labelFr: 'Évènement de trajet',
        labelEn: 'Trip event',
        category: 'event',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'enum',
        enumLabels: {
            0: { fr: 'Fin de trajet', en: 'Trip ended' },
            1: { fr: 'Début de trajet', en: 'Trip started' },
            2: { fr: 'Trajet professionnel', en: 'Business trip' },
            3: { fr: 'Trajet privé', en: 'Private trip' },
            4: { fr: 'Statut personnalisé 4', en: 'Custom status 4' },
            5: { fr: 'Statut personnalisé 5', en: 'Custom status 5' },
            6: { fr: 'Statut personnalisé 6', en: 'Custom status 6' },
            7: { fr: 'Statut personnalisé 7', en: 'Custom status 7' },
            8: { fr: 'Statut personnalisé 8', en: 'Custom status 8' },
            9: { fr: 'Statut personnalisé 9', en: 'Custom status 9' },
        },
    },
    {
        key: 'green_driving_type',
        unit: 'none',
        labelFr: 'Type d’évènement éco-conduite',
        labelEn: 'Green driving event type',
        category: 'event',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'enum',
        enumLabels: {
            1: { fr: 'Accélération brusque', en: 'Harsh acceleration' },
            2: { fr: 'Freinage brusque', en: 'Harsh braking' },
            3: { fr: 'Virage brusque', en: 'Harsh cornering' },
        },
    },
    {
        key: 'green_driving_value',
        unit: 'g_force',
        labelFr: 'Intensité de l’évènement éco-conduite',
        labelEn: 'Green driving event magnitude',
        category: 'event',
        aggregation: 'max',
        precision: 2,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
        // Bounded by the element's own width (1 byte ÷ 100), so no range is declared:
        // a value outside 0–2.55 is impossible rather than implausible.
    },
    {
        key: 'green_driving_duration',
        unit: 'millisecond',
        labelFr: 'Durée de l’évènement éco-conduite',
        labelEn: 'Green driving event duration',
        category: 'event',
        aggregation: 'max',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'over_speeding',
        unit: 'kph',
        labelFr: 'Survitesse',
        labelEn: 'Over-speeding',
        category: 'event',
        aggregation: 'max',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'din2_ain2_event',
        unit: 'logic',
        labelFr: 'Évènement DIN2/AIN2',
        labelEn: 'DIN2/AIN2 event',
        category: 'event',
        aggregation: 'max',
        precision: 0,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'auto_geofence',
        unit: 'none',
        labelFr: 'Géorepérage automatique',
        labelEn: 'Auto geofence',
        category: 'geofence',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'enum',
        enumLabels: {
            0: { fr: 'Sortie de zone', en: 'Left zone' },
            1: { fr: 'Entrée en zone', en: 'Entered zone' },
        },
    },
    // ── Codec 8E identity and diagnostics ─────────────────────────────────────
    // Everything below arrives only in the Codec 8E variable-length ("X byte") block or
    // above the one-byte id field, so none of it can exist on plain Codec 8.
    {
        key: 'vin',
        unit: 'text',
        labelFr: 'VIN (OBD interne)',
        labelEn: 'VIN (internal OBD)',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        // 17 ASCII bytes. A VIN identifies a vehicle, not a person, so it is not personal
        // data — but it is the field that proves the adapter is talking to the right car.
        valueType: 'text',
    },
    {
        key: 'vin_can',
        unit: 'text',
        labelFr: 'VIN (adaptateur CAN)',
        labelEn: 'VIN (CAN adapter)',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'text',
    },
    {
        key: 'dtc_codes',
        unit: 'text',
        labelFr: 'Codes défaut actifs',
        labelEn: 'Active fault codes',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        // Already-formatted mnemonics separated by commas ("P0301,P0420"), not a packed
        // count — see `dtc.ts`. `obd_dtc_count` (id 30) is the count and is a different
        // channel; neither is derivable from the other.
        valueType: 'text',
    },
    {
        key: 'crash_trace',
        unit: 'none',
        labelFr: 'Trace de choc (brute)',
        labelEn: 'Crash trace (raw)',
        category: 'event',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        // Up to 1200 bytes of accelerometer history. Opaque: the record layout is not in
        // any source read, and a wrongly-split trace is evidence in a liability dispute.
        valueType: 'opaque',
    },
    {
        key: 'crash_event_counter',
        unit: 'count',
        labelFr: 'Compteur d’évènements de choc',
        labelEn: 'Crash event counter',
        category: 'event',
        aggregation: 'max',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'instant_movement',
        unit: 'logic',
        labelFr: 'Mouvement instantané',
        labelEn: 'Instant movement',
        category: 'motion',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'driving_state',
        unit: 'none',
        labelFr: 'État de conduite',
        labelEn: 'Driving state',
        category: 'motion',
        aggregation: 'last',
        precision: 0,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'enum',
        enumLabels: {
            1: { fr: 'Contact mis', en: 'Ignition on' },
            2: { fr: 'En conduite', en: 'Driving' },
            3: { fr: 'Contact coupé', en: 'Ignition off' },
        },
    },
    {
        key: 'driving_records',
        unit: 'count',
        labelFr: 'Enregistrements de conduite',
        labelEn: 'Driving records',
        category: 'motion',
        aggregation: 'max',
        precision: 0,
        availability: 'optional',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'ignition_on_counter',
        unit: 'second',
        labelFr: 'Compteur de contact mis',
        labelEn: 'Ignition on counter',
        category: 'engine',
        aggregation: 'max',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        // SECONDS on the wire, where engine_worktime (id 102) is MINUTES. The two sit next
        // to each other on a vehicle page; storing either in the other's unit is a 60×
        // error that reads as plausible.
        valueType: 'number',
    },
    {
        key: 'private_mode',
        unit: 'logic',
        labelFr: 'Mode privé',
        labelEn: 'Private mode',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'digital_output_3',
        unit: 'logic',
        labelFr: 'Sortie numérique 3',
        labelEn: 'Digital output 3',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    {
        key: 'ground_sense',
        unit: 'logic',
        labelFr: 'Détection de masse',
        labelEn: 'Ground sense',
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'always',
        showInRawPanel: true,
        valueType: 'boolean',
    },
    // ── OEM / OBD counters read over the manufacturer's own PIDs ──────────────
    {
        key: 'oem_total_mileage',
        unit: 'kilometre',
        labelFr: 'Kilométrage constructeur (OEM)',
        labelEn: 'OEM total mileage',
        category: 'motion',
        aggregation: 'max',
        precision: 0,
        // KILOMETRES, where total_mileage_can (id 87) and total_odometer (id 16) are
        // METRES. Invariant: the mileage report shows the divergence between the three
        // rather than picking one, because a large drift is a sensor fault.
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'oem_distance_until_service',
        unit: 'kilometre',
        labelFr: 'Distance avant entretien (OEM)',
        labelEn: 'OEM distance until service',
        category: 'motion',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'oem_remaining_distance',
        unit: 'kilometre',
        labelFr: 'Autonomie restante (OEM)',
        labelEn: 'OEM remaining distance',
        category: 'motion',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'range_on_battery',
        unit: 'metre',
        labelFr: 'Autonomie sur batterie',
        labelEn: 'Range on battery',
        category: 'motion',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'range_on_additional_fuel',
        unit: 'metre',
        labelFr: 'Autonomie sur carburant additionnel',
        labelEn: 'Range on additional fuel',
        category: 'motion',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'oem_fuel_level_l',
        unit: 'litre',
        labelFr: 'Niveau de carburant (OEM, litres)',
        labelEn: 'OEM fuel level (litres)',
        category: 'fuel',
        aggregation: 'last',
        precision: 1,
        // A third fuel-level channel, deliberately not merged with fuel_level_l (id 84,
        // CAN adapter) or fuel_level_pct (id 89). Three sensors, three error models: a
        // theft alert has to know which one it is accusing someone on.
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'fuel_type',
        unit: 'none',
        labelFr: 'Type de carburant',
        labelEn: 'Fuel type',
        category: 'fuel',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'enum',
        enumLabels: {
            0: { fr: 'Non disponible', en: 'Not available' },
            1: { fr: 'Essence', en: 'Gasoline' },
            2: { fr: 'Méthanol', en: 'Methanol' },
            3: { fr: 'Éthanol', en: 'Ethanol' },
            4: { fr: 'Diesel', en: 'Diesel' },
            5: { fr: 'GPL', en: 'LPG' },
            6: { fr: 'GNC', en: 'CNG' },
            7: { fr: 'Propane', en: 'Propane' },
            8: { fr: 'Électrique', en: 'Electric' },
        },
    },
    {
        key: 'lpg_used_total',
        unit: 'litre',
        labelFr: 'GPL consommé (total)',
        labelEn: 'LPG used (total)',
        category: 'fuel',
        aggregation: 'max',
        precision: 1,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'lpg_used_counted',
        unit: 'litre',
        labelFr: 'GPL consommé (compteur adaptateur)',
        labelEn: 'LPG used (counted)',
        category: 'fuel',
        aggregation: 'max',
        precision: 1,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'lpg_level_pct',
        unit: 'percent',
        labelFr: 'Niveau GPL',
        labelEn: 'LPG level',
        category: 'fuel',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 100 },
    },
    {
        key: 'lpg_level_l',
        unit: 'litre',
        labelFr: 'Niveau GPL (litres)',
        labelEn: 'LPG level (litres)',
        category: 'fuel',
        aggregation: 'last',
        precision: 1,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'lng_used_total',
        unit: 'kilogram',
        labelFr: 'GNL consommé (total)',
        labelEn: 'LNG used (total)',
        category: 'fuel',
        aggregation: 'max',
        precision: 1,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'lng_used_counted',
        unit: 'kilogram',
        labelFr: 'GNL consommé (compteur adaptateur)',
        labelEn: 'LNG used (counted)',
        category: 'fuel',
        aggregation: 'max',
        precision: 1,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'lng_level_pct',
        unit: 'percent',
        labelFr: 'Niveau GNL',
        labelEn: 'LNG level',
        category: 'fuel',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 100 },
    },
    {
        key: 'lng_level_mass',
        unit: 'kilogram',
        labelFr: 'Niveau GNL (masse)',
        labelEn: 'LNG level (mass)',
        category: 'fuel',
        aggregation: 'last',
        precision: 1,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'throttle_position_group',
        unit: 'percent',
        labelFr: 'Position papillon',
        labelEn: 'Throttle position',
        category: 'engine',
        aggregation: 'avg',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 102 },
    },
    {
        key: 'commanded_equivalence_ratio',
        unit: 'dimensionless',
        labelFr: 'Rapport d’équivalence commandé',
        labelEn: 'Commanded equivalence ratio',
        category: 'engine',
        aggregation: 'avg',
        precision: 2,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'hybrid_system_voltage',
        unit: 'volt',
        labelFr: 'Tension du système hybride',
        labelEn: 'Hybrid system voltage',
        category: 'electrical',
        aggregation: 'avg',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'hybrid_system_current',
        unit: 'ampere',
        labelFr: 'Courant du système hybride',
        labelEn: 'Hybrid system current',
        category: 'electrical',
        aggregation: 'avg',
        precision: 0,
        // Signed: a hybrid pack recharging draws a negative current, and reading it
        // unsigned turns regenerative braking into a 65 000 A discharge.
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'oem_battery_charge_state',
        unit: 'none',
        labelFr: 'État de charge (batterie OEM)',
        labelEn: 'OEM battery charge state',
        category: 'electrical',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'enum',
        enumLabels: {
            0: { fr: 'Pas en charge', en: 'Not charging' },
            1: { fr: 'En charge', en: 'Charging' },
        },
    },
    {
        key: 'oem_battery_charge_level',
        unit: 'percent',
        labelFr: 'Niveau de charge (batterie OEM)',
        labelEn: 'OEM battery charge level',
        category: 'electrical',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 100 },
    },
    {
        key: 'oem_battery_power_consumption',
        unit: 'kwh_per_100km',
        labelFr: 'Consommation électrique (OEM)',
        labelEn: 'OEM battery power consumption',
        category: 'electrical',
        aggregation: 'avg',
        precision: 1,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
    },
    {
        key: 'oem_battery_state_of_health',
        unit: 'percent',
        labelFr: 'État de santé (batterie OEM)',
        labelEn: 'OEM battery state of health',
        category: 'electrical',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: 0, max: 100 },
    },
    {
        key: 'oem_battery_temperature',
        unit: 'celsius',
        labelFr: 'Température (batterie OEM)',
        labelEn: 'OEM battery temperature',
        category: 'temperature',
        aggregation: 'avg',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'number',
        range: { min: -60, max: 150 },
    },
    // ── ALL-CAN300 protocol-4 flag words ──────────────────────────────────────
    // Eight-byte bit vectors. Kept opaque on purpose: the bit-to-meaning legend was not
    // in any source read, and the same firmware publishes every one of those bits as its
    // own AVL id (see CAN_STATE_FLAGS), so bit-picking these buys nothing and can only
    // be wrong. Never scale or average a flag word — the result looks like a number.
    ...[
        ['state_flags_p4_security', 'sécurité', 'Security'],
        ['state_flags_p4_control', 'commande', 'Control'],
        ['state_flags_p4_indicator', 'témoins', 'Indicator'],
        ['state_flags_p4_agricultural', 'agricoles', 'Agricultural'],
        ['state_flags_p4_utility', 'voirie', 'Utility'],
        ['state_flags_p4_cistern', 'citerne', 'Cistern'],
    ].map(([key, fr, en]) => ({
        key,
        unit: 'bitmask',
        labelFr: `Drapeaux d’état ${fr} (P4)`,
        labelEn: `${en} state flags (P4)`,
        category: 'device',
        aggregation: 'last',
        precision: 0,
        availability: 'can',
        showInRawPanel: true,
        valueType: 'bitmask',
    })),
];
export const CHANNELS = [
    ...CORE_CHANNELS,
    ...canStateFlagChannels(),
    ...geofenceZoneChannels(),
];
const CHANNELS_BY_KEY = new Map(CHANNELS.map((channel) => [channel.key, channel]));
export function listChannels(filter) {
    if (filter === undefined)
        return CHANNELS;
    return CHANNELS.filter((channel) => {
        if (filter.category !== undefined && channel.category !== filter.category)
            return false;
        if (filter.availability !== undefined && channel.availability !== filter.availability) {
            return false;
        }
        if (filter.showInRawPanel !== undefined && channel.showInRawPanel !== filter.showInRawPanel) {
            return false;
        }
        if (filter.personalData !== undefined &&
            (channel.personalData ?? false) !== filter.personalData) {
            return false;
        }
        return true;
    });
}
export function getChannel(key) {
    return CHANNELS_BY_KEY.get(key);
}
/** `io_47` → 47. Returns null for anything that is not an unmapped-element key. */
export function unmappedAvlIdFromChannelKey(key) {
    const match = /^io_(\d+)$/.exec(key);
    if (match === null)
        return null;
    const id = Number(match[1]);
    return Number.isSafeInteger(id) ? id : null;
}
/**
 * Label for any channel key, including the `io_<id>` keys that unmapped elements
 * produce. Falling back to the bare key would print `io_47` in a customer's report;
 * naming it "E/S 47 (non mappée)" tells the reader it is real data whose meaning is
 * not yet known, which is the truth invariant 5 exists to preserve.
 */
export function channelLabel(key, locale) {
    const channel = CHANNELS_BY_KEY.get(key);
    if (channel !== undefined)
        return locale === 'fr' ? channel.labelFr : channel.labelEn;
    const unmappedId = unmappedAvlIdFromChannelKey(key);
    if (unmappedId !== null) {
        return locale === 'fr' ? `E/S ${unmappedId} (non mappée)` : `I/O ${unmappedId} (unmapped)`;
    }
    return key;
}
/** Label for an enum channel's value, or null when the value has no declared label. */
export function channelEnumLabel(key, value, locale) {
    const labels = CHANNELS_BY_KEY.get(key)?.enumLabels;
    const label = labels?.[value];
    if (label === undefined)
        return null;
    return locale === 'fr' ? label.fr : label.en;
}
/**
 * Whether a physical value sits outside the channel's declared plausibility range.
 * Channels with no declared range never report out-of-range — see ChannelRange for
 * why silence beats a range copied from a corrupt vendor cell.
 */
export function isOutOfRange(key, value) {
    if (value === null || !Number.isFinite(value))
        return false;
    const range = CHANNELS_BY_KEY.get(key)?.range;
    if (range === undefined)
        return false;
    return value < range.min || value > range.max;
}
/**
 * FALSE, and it is meant to stay false until someone bench-tests it.
 *
 * AVL id 90 "Door Status" is a two-byte word and none of the sources behind this
 * package states which bit is which door. Publishing an invented legend would produce
 * "porte arrière droite ouverte" from a bit that actually means something else — a
 * claim about a specific door on a specific vehicle, made up, and indistinguishable
 * from a real one. `door_status` is therefore stored as an opaque bitmask, and the
 * named door booleans come from ids 654–658 / 909 / 913, which the vendor names
 * individually. See NOTES-PHASE4.md.
 */
export const DOOR_STATUS_BITMASK_LEGEND_VERIFIED = false;
/** Named door channels, in the order a UI should list them. */
export const DOOR_FLAG_CHANNELS = [
    'ssf_door_front_left',
    'ssf_door_front_right',
    'ssf_door_rear_left',
    'ssf_door_rear_right',
    'ssf_door_trunk',
    'ssf_engine_cover_open',
    'ssf_roof_open',
];
/**
 * Decomposes the door flags out of a channel→value snapshot.
 *
 * Takes the snapshot rather than a bitmask because the bitmask's legend is unknown
 * (see above) while the individual ids are transcribed. Accepts numbers because that
 * is what the telemetry table stores, and booleans because that is what a live-state
 * projection holds.
 */
export function decomposeDoorStatus(snapshot) {
    const doors = {
        ssf_door_front_left: null,
        ssf_door_front_right: null,
        ssf_door_rear_left: null,
        ssf_door_rear_right: null,
        ssf_door_trunk: null,
        ssf_engine_cover_open: null,
        ssf_roof_open: null,
    };
    const reported = [];
    for (const key of DOOR_FLAG_CHANNELS) {
        const raw = snapshot[key];
        if (raw === undefined || raw === null)
            continue;
        if (typeof raw === 'number' && !Number.isFinite(raw))
            continue;
        doors[key] =
            raw === true || raw === 1 ? true : raw === false || raw === 0 ? false : Boolean(raw);
        reported.push(key);
    }
    const anyOpen = reported.length === 0 ? null : reported.some((key) => doors[key] === true);
    return { doors, anyOpen, reportedChannels: reported };
}
//# sourceMappingURL=registry.js.map